<script setup lang="ts">
  /**
   * InfoStrip — horizontally scrollable row of small utility cards
   * (weather, top stories per category, what to play, ...).
   *
   * Cards with a `description` expand into a Yahoo-style hover popover:
   * a larger panel below the card with the kicker as heading, the image,
   * and the longer text. The popover is position:fixed (measured from the
   * hovered card) so it escapes the scroller's overflow clipping while
   * staying inside the themed DOM subtree.
   *
   * Cards and the popover's "Read more" both open in a new tab; see linkRel.
   */
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
  import { resolveRegisteredComponent } from '../../registries/componentRegistry'
  import type { InfoStripItem, InfoStripProps } from './types'

  const props = withDefaults(defineProps<InfoStripProps>(), {
    items: () => [],
  })

  // Ad components contributed at runtime by the hosting app (common
  // registers both at boot). Undefined in standalone lego-land, where
  // the strip simply has no ad card. AdSlot serves the standard unit at
  // tablet and up; PortalFeedAd dresses an API native as a strip tile on
  // mobile, where the standard unit would collide with the top banner.
  const AdSlot = resolveRegisteredComponent('AdSlot')
  const PortalFeedAd = resolveRegisteredComponent('PortalFeedAd')

  /**
   * Where the ad card sits in the row (how many cards precede it). Falls
   * back past the end, i.e. the last card, when no position is configured.
   */
  const adPosition = computed(() => {
    const hasRenderer =
      Boolean(AdSlot) || Boolean(props.adSlot?.nativePlacement && PortalFeedAd)
    if (!props.adSlot || !hasRenderer) return -1
    return Math.min(
      props.adSlot.position ?? props.items.length,
      props.items.length
    )
  })

  const scroller = ref<HTMLElement | null>(null)
  const canScrollBack = ref(false)
  const canScrollForward = ref(true)

  function updateScrollState() {
    const el = scroller.value
    if (!el) return
    canScrollBack.value = el.scrollLeft > 4
    canScrollForward.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 4
  }

  onMounted(updateScrollState)

  function scrollForward() {
    scroller.value?.scrollBy({ left: 480, behavior: 'smooth' })
  }

  function scrollBack() {
    scroller.value?.scrollBy({ left: -480, behavior: 'smooth' })
  }

  // --- Hover popover ------------------------------------------------------

  const POPOVER_WIDTH = 400
  const OPEN_DELAY_MS = 150
  const CLOSE_DELAY_MS = 200

  const hoveredId = ref<string | null>(null)
  const popoverPosition = ref({ left: 0, top: 0 })
  let openTimer: ReturnType<typeof setTimeout> | null = null
  let closeTimer: ReturnType<typeof setTimeout> | null = null

  // A card unmounted mid hover (a category switch rebuilds the strip) would
  // otherwise leave a timer to fire against a dead component
  onBeforeUnmount(() => {
    if (openTimer) clearTimeout(openTimer)
    if (closeTimer) clearTimeout(closeTimer)
  })

  const hoveredItem = computed(() =>
    props.items.find(
      (item) => item.id === hoveredId.value && Boolean(item.description)
    )
  )

  function openPopover(item: InfoStripItem, event: Event) {
    if (!item.description) return
    if (closeTimer) {
      clearTimeout(closeTimer)
      closeTimer = null
    }
    if (openTimer) clearTimeout(openTimer)

    const card = event.currentTarget
    if (!(card instanceof HTMLElement)) return
    const rect = card.getBoundingClientRect()

    openTimer = setTimeout(() => {
      const maxLeft = window.innerWidth - POPOVER_WIDTH - 16
      popoverPosition.value = {
        left: Math.max(16, Math.min(rect.left, maxLeft)),
        top: rect.bottom + 8,
      }
      hoveredId.value = item.id
    }, OPEN_DELAY_MS)
  }

  /** Escape dismisses immediately rather than waiting out the hover delay */
  function closePopover() {
    if (openTimer) {
      clearTimeout(openTimer)
      openTimer = null
    }
    if (closeTimer) {
      clearTimeout(closeTimer)
      closeTimer = null
    }
    hoveredId.value = null
  }

  function scheduleClose() {
    if (openTimer) {
      clearTimeout(openTimer)
      openTimer = null
    }
    if (closeTimer) clearTimeout(closeTimer)
    closeTimer = setTimeout(() => {
      hoveredId.value = null
    }, CLOSE_DELAY_MS)
  }

  function cancelClose() {
    if (closeTimer) {
      clearTimeout(closeTimer)
      closeTimer = null
    }
  }

  // --- Links --------------------------------------------------------------

  /**
   * Every card link opens in a new tab and leaves the portal untouched, the
   * same way the platform's own content navigation does (useRedirectActions
   * navigates with `{ open: { target: '_blank' } }`). Third-party hosts also
   * get `noreferrer`; our own same-origin article pages keep the referrer.
   */
  function linkRel(href: string): string {
    return href.startsWith('http') ? 'noopener noreferrer' : 'noopener'
  }
</script>

<template>
  <div data-portal-module="strip" class="relative w-full">
    <ul
      ref="scroller"
      class="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      @scroll.passive="updateScrollState"
    >
      <template v-for="(item, index) in items" :key="item.id">
        <!-- Ad card inserted mid-row (SUP-668): same 312px card footprint
             as every other card, so the row reads as one uniform strip.
             At tablet and up it serves the standard unit; AY indexes
             multiple instances of one placement, so it coexists with the
             rail's instance at desktop. Below tablet the top banner owns
             the unit (the AdSlot's minViewport gate) and the card renders
             the API native tile instead. -->
        <li
          v-if="adSlot && index === adPosition"
          class="flex shrink-0 items-center"
        >
          <div
            v-if="AdSlot"
            class="hidden h-20 items-center rounded-lg bg-surface-primary p-1.5 tablet:flex"
          >
            <component
              :is="AdSlot"
              :placement="adSlot.placement"
              :ad-type="adSlot.adType"
              :width="adSlot.width"
              :height="adSlot.height"
              :min-viewport="adSlot.minViewport"
            />
          </div>
          <component
            :is="PortalFeedAd"
            v-if="adSlot.nativePlacement && PortalFeedAd"
            class="tablet:hidden"
            :placement="adSlot.nativePlacement"
            view="strip"
          />
        </li>

        <li class="shrink-0">
          <component
            :is="item.href ? 'a' : 'div'"
            :href="item.href"
            :target="item.href ? '_blank' : undefined"
            :rel="item.href ? linkRel(item.href) : undefined"
            class="flex h-20 w-[312px] cursor-pointer items-start justify-between gap-3 rounded-lg bg-surface-primary p-3 transition-colors hover:bg-[var(--yp-card-hover)]"
            :data-strip-card="item.id"
            :data-strip-image="item.imageUrl"
            @mouseenter="openPopover(item, $event)"
            @mouseleave="scheduleClose"
            @focus="openPopover(item, $event)"
            @blur="scheduleClose"
            @keydown.esc="closePopover"
          >
            <div class="min-w-0">
              <div class="flex items-center gap-1.5">
                <span class="truncate text-xs text-content-secondary">
                  {{ item.kicker }}
                </span>
                <span
                  v-if="item.badge"
                  class="shrink-0 rounded bg-[var(--yp-active)] px-1.5 py-0.5 text-[9px] font-bold uppercase text-content-primary"
                >
                  {{ item.badge }}
                </span>
              </div>
              <p
                class="mt-1 line-clamp-2 text-[13px] font-semibold leading-snug text-content-primary"
              >
                {{ item.title }}
                <span
                  v-if="item.delta"
                  :class="
                    item.deltaPositive ? 'text-[#4ade80]' : 'text-[#f87171]'
                  "
                >
                  {{ item.delta }}
                </span>
              </p>
            </div>
            <!-- Text anchors to the card top; the visual stays vertically
                 centered so short titles do not leave it hanging high -->
            <div
              v-if="item.icon"
              class="flex shrink-0 flex-col items-center gap-0.5 self-center"
            >
              <UIcon :name="item.icon" class="h-6 w-6 text-[#ffd66b]" />
              <span
                v-if="item.iconLabel"
                class="text-[13px] font-bold text-content-primary"
              >
                {{ item.iconLabel }}
              </span>
            </div>
            <img
              v-else-if="item.imageUrl"
              :src="item.imageUrl"
              alt=""
              class="h-14 w-14 shrink-0 self-center rounded-lg object-cover"
              loading="lazy"
            />
          </component>
        </li>
      </template>

      <!-- Trailing ad card, only when no mid-row position is configured -->
      <li
        v-if="adSlot && adPosition === items.length"
        class="flex shrink-0 items-center"
      >
        <div
          v-if="AdSlot"
          class="hidden h-20 items-center rounded-lg bg-surface-primary p-1.5 tablet:flex"
        >
          <component
            :is="AdSlot"
            :placement="adSlot.placement"
            :ad-type="adSlot.adType"
            :width="adSlot.width"
            :height="adSlot.height"
            :min-viewport="adSlot.minViewport"
          />
        </div>
        <component
          :is="PortalFeedAd"
          v-if="adSlot.nativePlacement && PortalFeedAd"
          class="tablet:hidden"
          :placement="adSlot.nativePlacement"
          view="strip"
        />
      </li>
    </ul>

    <!-- Hover popover: expanded card, like Yahoo's Today in History.
         Springs open from the card (scale + rise, expo-out easing). -->
    <Transition
      enter-active-class="transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
      enter-from-class="-translate-y-3 scale-90 opacity-0"
      enter-to-class="translate-y-0 scale-100 opacity-100"
      leave-active-class="transition-[opacity,transform] duration-150 ease-in"
      leave-to-class="-translate-y-1 scale-95 opacity-0"
    >
      <div
        v-if="hoveredItem"
        class="fixed z-50 w-[400px] origin-top rounded-xl border border-[var(--yp-border)] bg-[var(--yp-menu)] p-5 shadow-2xl"
        :style="{
          left: `${popoverPosition.left}px`,
          top: `${popoverPosition.top}px`,
        }"
        @mouseenter="cancelClose"
        @mouseleave="scheduleClose"
      >
        <div class="flex items-center justify-between gap-3">
          <h3 class="text-lg font-bold text-content-primary">
            {{ hoveredItem.kicker }}
          </h3>
          <div
            v-if="hoveredItem.icon"
            class="flex items-center gap-1.5 text-content-secondary"
          >
            <UIcon :name="hoveredItem.icon" class="h-5 w-5 text-[#ffd66b]" />
            <span
              v-if="hoveredItem.iconLabel"
              class="text-sm font-bold text-content-primary"
            >
              {{ hoveredItem.iconLabel }}
            </span>
          </div>
        </div>

        <img
          v-if="hoveredItem.imageUrl"
          :src="hoveredItem.imageUrl"
          alt=""
          class="mt-3 aspect-[16/9] w-full rounded-lg object-cover"
          loading="lazy"
        />

        <p class="mt-3 text-[15px] font-semibold text-content-primary">
          {{ hoveredItem.title }}
        </p>
        <p
          class="mt-1.5 line-clamp-4 text-sm leading-relaxed text-content-secondary"
        >
          {{ hoveredItem.description }}
        </p>

        <div
          v-if="hoveredItem.href"
          class="mt-4 flex items-center justify-end border-t border-[var(--yp-divider)] pt-3"
        >
          <a
            :href="hoveredItem.href"
            target="_blank"
            :rel="linkRel(hoveredItem.href)"
            class="text-sm font-semibold text-content-primary hover:underline"
          >
            Read more
          </a>
        </div>
      </div>
    </Transition>

    <button
      v-if="canScrollBack"
      type="button"
      aria-label="Scroll back"
      class="absolute -left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--yp-border)] bg-[var(--yp-menu)] shadow-lg transition-colors hover:bg-[var(--yp-card-hover)]"
      @click="scrollBack"
    >
      <UIcon
        name="i-heroicons-chevron-left"
        class="h-5 w-5 text-content-primary"
      />
    </button>
    <button
      v-if="canScrollForward"
      type="button"
      aria-label="Scroll for more"
      class="absolute -right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--yp-border)] bg-[var(--yp-menu)] shadow-lg transition-colors hover:bg-[var(--yp-card-hover)]"
      @click="scrollForward"
    >
      <UIcon
        name="i-heroicons-chevron-right"
        class="h-5 w-5 text-content-primary"
      />
    </button>
  </div>
</template>
